import { canDo, FormSubmissionSignatureContext, SignatureAction } from 'src/types/signatureContext';

export type SignPermissionResult = { label: string; value: boolean };

/**
 * Derives the therapist-column Sign-button label + actionability from a document's
 * signatureContext. Used by therapist-side document tables (signature-pending-list,
 * other-signature-document) where the column renders the therapist signer only — so
 * the check is scoped to allowedTherapistSignatureActions to avoid leaking another
 * role's CAN_SIGN into the therapist column. All identity, ordering, and lockdown
 * rules live on the BE — the FE only reads CAN_SIGN off the action list.
 */
export const checkSignPermission = (document: any): SignPermissionResult => {
  const ctx = document?.signatureContext;
  if (canDo(ctx?.allowedTherapistSignatureActions, SignatureAction.CAN_SIGN)) {
    return { label: 'Sign document', value: true };
  }
  return {
    label: document?.status === 'COMPLETED' ? 'Signed' : 'Pending',
    value: false,
  };
};

/** Attachment-type radio values used by the wet/paper signature upload flow (Physician-Signature page). */
export type PaperSignatureType = 'PhysicianSignature' | 'SUPERVISOR_SIGNATURE';

export type PaperSignatureEligibility = { eligible: boolean; reason: string };

const COMPLETED = 'COMPLETED';

/**
 * THIN-7550: whether a wet/paper signature of the given attachment type can be applied to this
 * document right now, mirroring the signature FSM ordering (therapist → supervisor → physician).
 *
 * The backend's per-axis {@code CAN_SIGN} capability is role-gated to the actual signer (supervisor /
 * physician), so it can't be reused for the wet-signature upload flow — those uploads are performed by
 * office staff / a DOR, not the signer. We therefore replicate the FSM legal-from ordering from the
 * per-axis {@code signStatus} + required flags already present on {@code document.signatureContext}:
 *  - SUPERVISOR_SIGN is legal only once the therapist has signed;
 *  - PHYSICIAN_SIGN is legal only once the therapist has signed AND (supervisor not required OR signed).
 *
 * Complements the backend "apply only when legal" guard (THIN-7431): the FE stops the user from
 * selecting a document the backend would no-op.
 */
export const checkPaperSignatureEligibility = (
  document: any,
  attachmentType: PaperSignatureType
): PaperSignatureEligibility => {
  const ctx: FormSubmissionSignatureContext | undefined = document?.signatureContext;
  if (!ctx) {
    return { eligible: false, reason: 'Signature details unavailable' };
  }

  const therapistSigned = ctx.therapist?.signStatus === COMPLETED;
  const supervisorSigned = ctx.supervisor?.signStatus === COMPLETED;
  const physicianSigned = ctx.physician?.signStatus === COMPLETED;

  if (attachmentType === 'SUPERVISOR_SIGNATURE') {
    if (!ctx.supervisorSignatureRequired || supervisorSigned) {
      return { eligible: false, reason: 'Supervisor signature not required' };
    }
    if (!therapistSigned) {
      return { eligible: false, reason: 'Therapist signature required first' };
    }
    return { eligible: true, reason: '' };
  }

  // PhysicianSignature
  if (!ctx.physicianSignatureRequired || physicianSigned) {
    return { eligible: false, reason: 'Physician signature not required' };
  }
  if (!therapistSigned) {
    return { eligible: false, reason: 'Therapist signature required first' };
  }
  if (ctx.supervisorSignatureRequired && !supervisorSigned) {
    return { eligible: false, reason: 'Supervisor signature required first' };
  }
  return { eligible: true, reason: '' };
};

export type WetSignatureBuckets = {
  physicianSignatureDocs: any[];
  supervisorSignatureDocs: any[];
};

/**
 * THIN-7575: split submitted documents into the two wet/paper-signature upload lists
 * (physician vs supervisor) purely from signatureContext — the BE owns the role-axis
 * assignment; docs predating dual-emit (no signatureContext) are skipped rather than
 * re-routed by a frontend formType rule.
 *
 * The two buckets are INDEPENDENT, not mutually exclusive: a document requiring BOTH a
 * supervisor and a physician cosign (e.g. an eval) belongs in both lists at once. An
 * earlier `else if` dropped such an eval from the supervisor list whenever it also
 * qualified for the physician list, stranding the supervisor's paper-signature upload.
 * Row-level {@link checkPaperSignatureEligibility} still grays the not-yet-eligible rows;
 * this only decides which list a document appears in.
 *
 * THIN-7578: a document is bucketed as soon as it needs a physician/supervisor wet
 * signature that isn't done — regardless of the therapist signature state. Previously the
 * physician bucket also required the therapist to have signed, so an Eval/Recert needing
 * therapist + physician (no supervisor) was invisible in the picker while the therapist
 * signature was pending, hiding upstream-blocked work from the DOR. The therapist-first
 * ordering is enforced at the row level by checkPaperSignatureEligibility ("Therapist
 * signature required first"), not by hiding the row.
 */
export const bucketWetSignatureDocs = (documents: any[]): WetSignatureBuckets => {
  const physicianSignatureDocs: any[] = [];
  const supervisorSignatureDocs: any[] = [];

  (documents ?? []).forEach((document: any) => {
    const ctx: FormSubmissionSignatureContext | undefined = document?.signatureContext;
    if (!ctx) {
      return;
    }
    if (ctx.physicianSignatureRequired && ctx.physician?.signStatus !== COMPLETED) {
      physicianSignatureDocs.push(document);
    }
    if (ctx.supervisorSignatureRequired && ctx.supervisor?.signStatus !== COMPLETED) {
      supervisorSignatureDocs.push(document);
    }
  });

  return { physicianSignatureDocs, supervisorSignatureDocs };
};
